import { gql } from '@apollo/client';

// ============================================
// Field Allocation Request Queries
// ============================================

export const GET_FIELD_ALLOCATION_REQUEST = gql`
  query GetFieldAllocationRequest($id: UUID!) {
    fieldAllocationRequest(id: $id) {
      id
      requestNumber
      requestDate
      employeeId
      employeeName
      employeeCode
      productId
      productCode
      productName
      unitOfMeasureId
      unitOfMeasure {
        id
        code
        name
        symbol
      }
      preAssignedQuantity
      requestedQuantity
      additionalQuantity
      approvedQuantity
      unitPrice
      additionalValue
      workOrderReference
      customerId
      customer {
        id
        customerName
      }
      jobLocation
      reasonId
      reason {
        id
        code
        displayName
      }
      status {
        id
        code
        displayName
      }
      approverId
      approverName
      approvedAt
      approvalRemarks
      rejectionReason {
        id
        code
        displayName
      }
      flagForReview
      overdueAt
      reminderCount
      createdAt
      modifiedAt
    }
  }
`;

export const GET_FIELD_ALLOCATION_REQUESTS = gql`
  query GetFieldAllocationRequests($filter: FieldAllocationFilterInput, $pagination: PaginationInput) {
    fieldAllocationRequests(filter: $filter, pagination: $pagination) {
      items {
        id
        requestNumber
        requestDate
        employeeName
        employeeCode
        productName
        productCode
        unitOfMeasureId
        unitOfMeasure {
          id
          symbol
        }
        additionalQuantity
        approvedQuantity
        additionalValue
        reasonId
        reason {
          id
          code
          displayName
        }
        status {
          id
          code
          displayName
        }
        approverName
        approvedAt
        overdueAt
        reminderCount
        createdAt
      }
      pageInfo {
        currentPage
        totalPages
        totalCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_MY_FIELD_ALLOCATIONS = gql`
  query GetMyFieldAllocations($filter: FieldAllocationFilterInput, $pagination: PaginationInput) {
    myFieldAllocationRequests(filter: $filter, pagination: $pagination) {
      items {
        id
        requestNumber
        requestDate
        productName
        productCode
        unitOfMeasureId
        unitOfMeasure {
          id
          symbol
        }
        preAssignedQuantity
        requestedQuantity
        additionalQuantity
        approvedQuantity
        reasonId
        reason {
          id
          code
          displayName
        }
        status {
          id
          code
          displayName
        }
        approverName
        approvedAt
        overdueAt
        createdAt
      }
      pageInfo {
        currentPage
        totalPages
        totalCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_PENDING_FIELD_ALLOCATIONS = gql`
  query GetPendingFieldAllocations($pagination: PaginationInput) {
    pendingFieldAllocations(pagination: $pagination) {
      items {
        id
        requestNumber
        requestDate
        employeeName
        employeeCode
        productName
        productCode
        unitOfMeasureId
        unitOfMeasure {
          id
          symbol
        }
        additionalQuantity
        additionalValue
        reasonId
        reason {
          id
          code
          displayName
        }
        workOrderReference
        customerId
        customer {
          id
          customerName
        }
        jobLocation
        status {
          id
          code
          displayName
        }
        overdueAt
        reminderCount
        createdAt
      }
      pageInfo {
        currentPage
        totalPages
        totalCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_EMPLOYEE_IN_POSSESSION = gql`
  query GetEmployeeInPossession($employeeId: UUID!, $productId: UUID!) {
    employeeInPossession(employeeId: $employeeId, productId: $productId) {
      employeeId
      productId
      threshold
      thresholdUnit
      totalIssued
      totalConsumed
      currentlyInPossession
      remainingIssuable
    }
  }
`;

// ============================================
// Field Allocation Request Mutations
// ============================================

export const CREATE_FIELD_ALLOCATION_REQUEST = gql`
  mutation CreateFieldAllocationRequest($input: CreateFieldAllocationInput!) {
    createFieldAllocationRequest(input: $input) {
      id
      requestNumber
      status {
        code
        displayName
      }
    }
  }
`;

export const APPROVE_FIELD_ALLOCATION = gql`
  mutation ApproveFieldAllocation($input: ApproveFieldAllocationInput!) {
    approveFieldAllocation(input: $input) {
      id
      requestNumber
      status {
        id
        code
        displayName
      }
      approvedQuantity
      approverName
      approvedAt
    }
  }
`;

export const REJECT_FIELD_ALLOCATION = gql`
  mutation RejectFieldAllocation($input: RejectFieldAllocationInput!) {
    rejectFieldAllocation(input: $input) {
      id
      requestNumber
      status {
        id
        code
        displayName
      }
    }
  }
`;

export const CANCEL_FIELD_ALLOCATION = gql`
  mutation CancelFieldAllocation($requestId: UUID!) {
    cancelFieldAllocation(requestId: $requestId) {
      id
      requestNumber
      status {
        code
        displayName
      }
    }
  }
`;

// ============================================
// Site Usage Queries & Mutations
// ============================================

export const GET_SITE_USAGE_RECORDS = gql`
  query GetSiteUsageRecords($filter: SiteUsageFilterInput, $pagination: PaginationInput) {
    siteUsageRecords(filter: $filter, pagination: $pagination) {
      items {
        id
        usageNumber
        usageDate
        employeeName
        employeeCode
        productName
        productCode
        unitOfMeasurement
        quantityUsed
        workOrderReference
        remarks
        createdAt
      }
      pageInfo {
        currentPage
        totalPages
        totalCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const RECORD_SITE_USAGE = gql`
  mutation RecordSiteUsage($input: CreateSiteUsageInput!) {
    recordSiteUsage(input: $input) {
      id
      usageNumber
      quantityUsed
    }
  }
`;

// ============================================
// Notification Queries & Mutations
// ============================================

export const GET_MY_NOTIFICATIONS = gql`
  query GetMyNotifications($unreadOnly: Boolean, $pagination: PaginationInput) {
    myNotifications(unreadOnly: $unreadOnly, pagination: $pagination) {
      items {
        id
        title
        body
        link
        isRead
        referenceType
        referenceId
        createdAt
      }
      pageInfo {
        currentPage
        totalPages
        totalCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_UNREAD_NOTIFICATION_COUNT = gql`
  query GetUnreadNotificationCount {
    unreadNotificationCount {
      count
    }
  }
`;

export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($notificationId: UUID!) {
    markNotificationRead(notificationId: $notificationId) {
      id
      isRead
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      success
      message
    }
  }
`;

// ============================================
// Lookups for FAR forms
// ============================================

export const GET_FAR_FORM_LOOKUPS = gql`
  query GetFARFormLookups {
    employees(pagination: { page: 1, limit: 500 }) {
      items {
        id
        employeeCode
        employeeName
      }
    }
    products(pagination: { page: 1, limit: 500 }) {
      items {
        id
        productCode
        productName
        unitOfMeasure {
          id
          name
          symbol
        }
      }
    }
    localCustomers(pagination: { page: 1, limit: 500 }) {
      items {
        id
        customerName
      }
    }
    requestReasons: lookupValues(typeCode: "FAR_REQUEST_REASON") {
      id
      code
      displayName
    }
    rejectionReasons: lookupValues(typeCode: "FIELD_ALLOC_REJECT_REASON") {
      id
      code
      displayName
    }
  }
`;
